import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { booksellerEntitlementConfigsService } from '../../../api/booksellerEntitlementConfigsService';
import { useBooksellerEntitlementConfig } from '../../../hooks/useBooksellerEntitlementConfig';
import { SensitiveField, type SensitiveFieldHandle } from './SensitiveField';
import {
  ApiAuthType,
  API_AUTH_TYPE_LABELS,
  ENTITLEMENT_ADAPTER_TYPE_LABELS,
  EntitlementAdapterType,
  type CreateBooksellerEntitlementConfigDto,
  type UpdateBooksellerEntitlementConfigDto,
} from '../../../types/booksellerEntitlementConfig';

interface Props { booksellerId: string; }

interface DraftFields {
  adapterType: EntitlementAdapterType;
  endpoint: string;
  httpMethod: string;
  authType: ApiAuthType;
  apiKeyHeaderName: string;
  requestMapping: string;
  responseMapping: string;
  retryPolicy: string;
  timeoutSeconds: number;
  cacheDurationMinutes: number;
  oAuthTokenEndpoint: string;
  oAuthClientId: string;
  oAuthScopes: string;
}

const EMPTY_DRAFT: DraftFields = {
  adapterType: EntitlementAdapterType.Configurable,
  endpoint: '',
  httpMethod: 'GET',
  authType: ApiAuthType.None,
  apiKeyHeaderName: '',
  requestMapping: '',
  responseMapping: '',
  retryPolicy: '',
  timeoutSeconds: 30,
  cacheDurationMinutes: 5,
  oAuthTokenEndpoint: '',
  oAuthClientId: '',
  oAuthScopes: '',
};

const TOKEN_HELP = '{email}, {userEmail}, {userId}, {userName}, {firstName}, {lastName}, {booksellerId}';

export default function EntitlementTab({ booksellerId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, error } = useBooksellerEntitlementConfig(booksellerId);

  const [draft, setDraft] = useState<DraftFields>(EMPTY_DRAFT);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyClear, setApiKeyClear] = useState(false);
  const [oauthSecret, setOauthSecret] = useState('');
  const [oauthSecretClear, setOauthSecretClear] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const apiKeyRef = useRef<SensitiveFieldHandle>(null);
  const oauthSecretRef = useRef<SensitiveFieldHandle>(null);

  // Hydrate draft when server data lands. No else branch: initial useState covers the
  // empty case, and we do NOT want to clobber user input if `data` flips to undefined
  // during a query invalidation between create-success and the refetched row landing.
  useEffect(() => {
    if (data) {
      setDraft({
        adapterType: data.adapterType ?? EntitlementAdapterType.Configurable,
        endpoint: data.endpoint ?? '',
        httpMethod: data.httpMethod ?? 'GET',
        authType: data.authType ?? ApiAuthType.None,
        apiKeyHeaderName: data.apiKeyHeaderName ?? '',
        requestMapping: data.requestMapping ?? '',
        responseMapping: data.responseMapping ?? '',
        retryPolicy: data.retryPolicy ?? '',
        timeoutSeconds: data.timeoutSeconds ?? 30,
        cacheDurationMinutes: data.cacheDurationMinutes ?? 5,
        oAuthTokenEndpoint: data.oAuthTokenEndpoint ?? '',
        oAuthClientId: data.oAuthClientId ?? '',
        oAuthScopes: data.oAuthScopes ?? '',
      });
      apiKeyRef.current?.reset();
      oauthSecretRef.current?.reset();
    }
  }, [data]);

  const hasStoredApiKey = !!data?.apiKey;
  const hasStoredOauthSecret = !!data?.oAuthClientSecret;

  const create = useMutation({
    mutationFn: () => {
      const payload: CreateBooksellerEntitlementConfigDto = {
        booksellerId,
        adapterType: draft.adapterType,
        endpoint: draft.endpoint,
        httpMethod: draft.httpMethod,
        authType: draft.authType,
        // Only send staged secrets for the currently selected auth-type section.
        apiKey: showApiKey ? (apiKey || undefined) : undefined,
        apiKeyHeaderName: draft.apiKeyHeaderName || undefined,
        requestMapping: draft.requestMapping || undefined,
        responseMapping: draft.responseMapping || undefined,
        retryPolicy: draft.retryPolicy || undefined,
        timeoutSeconds: draft.timeoutSeconds,
        cacheDurationMinutes: draft.cacheDurationMinutes,
        oAuthTokenEndpoint: draft.oAuthTokenEndpoint || undefined,
        oAuthClientId: draft.oAuthClientId || undefined,
        oAuthClientSecret: showOauth ? (oauthSecret || undefined) : undefined,
        oAuthScopes: draft.oAuthScopes || undefined,
      };
      return booksellerEntitlementConfigsService.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-bookseller-entitlement-config', booksellerId] });
      setSaveError(null);
      setSaveSuccess(true);
      setApiKey(''); setOauthSecret('');
    },
    onError: (err: unknown) => {
      setSaveSuccess(false);
      setSaveError(readErrorMessage(err));
    },
  });

  const update = useMutation({
    mutationFn: () => {
      if (!data) throw new Error('No existing config to update');
      const apiKeyTouched = apiKeyRef.current?.hasBeenTouched() ?? false;
      const oauthTouched = oauthSecretRef.current?.hasBeenTouched() ?? false;
      // When the auth-type section is currently hidden, never send its staged secret
      // even if the user previously typed into that field before switching auth types.
      const apiKeyToSend = !showApiKey
        ? undefined
        : !apiKeyTouched
          ? undefined
          : (apiKey === '' && apiKeyClear ? '' : (apiKey || undefined));
      const oauthSecretToSend = !showOauth
        ? undefined
        : !oauthTouched
          ? undefined
          : (oauthSecret === '' && oauthSecretClear ? '' : (oauthSecret || undefined));

      const payload: UpdateBooksellerEntitlementConfigDto = {
        booksellerId,
        adapterType: draft.adapterType,
        endpoint: draft.endpoint,
        httpMethod: draft.httpMethod,
        authType: draft.authType,
        apiKey: apiKeyToSend,
        apiKeyHeaderName: draft.apiKeyHeaderName || undefined,
        requestMapping: draft.requestMapping || undefined,
        responseMapping: draft.responseMapping || undefined,
        retryPolicy: draft.retryPolicy || undefined,
        timeoutSeconds: draft.timeoutSeconds,
        cacheDurationMinutes: draft.cacheDurationMinutes,
        oAuthTokenEndpoint: draft.oAuthTokenEndpoint || undefined,
        oAuthClientId: draft.oAuthClientId || undefined,
        oAuthClientSecret: oauthSecretToSend,
        oAuthScopes: draft.oAuthScopes || undefined,
        concurrencyStamp: data.concurrencyStamp,
      };
      return booksellerEntitlementConfigsService.update(data.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-bookseller-entitlement-config', booksellerId] });
      setSaveError(null);
      setSaveSuccess(true);
      setApiKey(''); setOauthSecret('');
      setApiKeyClear(false); setOauthSecretClear(false);
      apiKeyRef.current?.reset(); oauthSecretRef.current?.reset();
    },
    onError: (err: unknown) => {
      setSaveSuccess(false);
      setSaveError(readErrorMessage(err));
    },
  });

  if (isLoading) return <p className="text-xs text-neutral-500">{t('App.Admin.Booksellers.EntitlementTab.Loading')}</p>;
  if (error) return <p className="text-xs text-red-600">{t('App.Admin.Booksellers.EntitlementTab.LoadError', { error: String((error as Error).message) })}</p>;

  const showApiKey = draft.authType === ApiAuthType.Bearer || draft.authType === ApiAuthType.ApiKey;
  const showOauth = draft.authType === ApiAuthType.ClientCredentials;
  const canSave = (draft.endpoint ?? '').trim().length > 0
    && (draft.httpMethod ?? '').trim().length > 0
    && (!!data || (
      (showApiKey ? apiKey.length > 0 : true)
      && (showOauth ? oauthSecret.length > 0 : true)
    ));

  return (
    <div className="space-y-3">
      {saveError && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">{saveError}</div>}
      {saveSuccess && <div role="status" className="bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2 rounded">{t('App.Admin.Booksellers.EntitlementTab.Saved')}</div>}

      <Field label={t('App.Admin.Booksellers.EntitlementTab.AdapterTypeLabel')}>
        <select className={textInput} value={draft.adapterType} onChange={(e) => setDraft({ ...draft, adapterType: Number(e.target.value) as EntitlementAdapterType })}>
          {Object.entries(ENTITLEMENT_ADAPTER_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value} disabled={Number(value) !== EntitlementAdapterType.Configurable}>{label}</option>
          ))}
        </select>
      </Field>

      <Field label={t('App.Admin.Booksellers.EntitlementTab.EndpointLabel')}><input className={textInput} type="url" value={draft.endpoint} onChange={(e) => setDraft({ ...draft, endpoint: e.target.value })} /></Field>
      <p className="text-xs text-neutral-400 -mt-2">{t('App.Admin.Booksellers.EntitlementTab.EndpointTokens', { tokens: TOKEN_HELP })}</p>

      <Field label={t('App.Admin.Booksellers.EntitlementTab.HttpMethodLabel')}>
        <select className={textInput} value={draft.httpMethod} onChange={(e) => setDraft({ ...draft, httpMethod: e.target.value })}>
          {['GET', 'POST', 'PUT', 'PATCH'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>

      <Field label={t('App.Admin.Booksellers.EntitlementTab.AuthTypeLabel')}>
        <select className={textInput} value={draft.authType} onChange={(e) => setDraft({ ...draft, authType: Number(e.target.value) as ApiAuthType })}>
          {Object.entries(API_AUTH_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </Field>

      {showApiKey && (
        <>
          <SensitiveField
            ref={apiKeyRef}
            name="apiKey"
            label={t('App.Admin.Booksellers.EntitlementTab.ApiKeyLabel')}
            required={!data}
            hasStoredValue={hasStoredApiKey}
            onChange={setApiKey}
            onConfirmClear={() => setApiKeyClear(true)}
          />
          <Field label={t('App.Admin.Booksellers.EntitlementTab.ApiKeyHeaderNameLabel')}><input className={textInput} value={draft.apiKeyHeaderName} onChange={(e) => setDraft({ ...draft, apiKeyHeaderName: e.target.value })} placeholder="Authorization" /></Field>
        </>
      )}

      {showOauth && (
        <>
          <Field label={t('App.Admin.Booksellers.EntitlementTab.OAuthTokenEndpointLabel')}><input className={textInput} type="url" value={draft.oAuthTokenEndpoint} onChange={(e) => setDraft({ ...draft, oAuthTokenEndpoint: e.target.value })} /></Field>
          <Field label={t('App.Admin.Booksellers.EntitlementTab.OAuthClientIdLabel')}><input className={textInput} value={draft.oAuthClientId} onChange={(e) => setDraft({ ...draft, oAuthClientId: e.target.value })} /></Field>
          <SensitiveField
            ref={oauthSecretRef}
            name="oauthClientSecret"
            label={t('App.Admin.Booksellers.EntitlementTab.OAuthClientSecretLabel')}
            required={!data}
            hasStoredValue={hasStoredOauthSecret}
            onChange={setOauthSecret}
            onConfirmClear={() => setOauthSecretClear(true)}
          />
          <Field label={t('App.Admin.Booksellers.EntitlementTab.OAuthScopesLabel')}><input className={textInput} value={draft.oAuthScopes} onChange={(e) => setDraft({ ...draft, oAuthScopes: e.target.value })} /></Field>
        </>
      )}

      <Field label={t('App.Admin.Booksellers.EntitlementTab.RequestMappingLabel')}>
        <textarea className={`${textInput} font-mono text-xs`} rows={6} value={draft.requestMapping} onChange={(e) => setDraft({ ...draft, requestMapping: e.target.value })} placeholder='{ "headers": {...}, "query": {...}, "body": "..." }' />
      </Field>

      <Field label={t('App.Admin.Booksellers.EntitlementTab.ResponseMappingLabel')}>
        <textarea className={`${textInput} font-mono text-xs`} rows={6} value={draft.responseMapping} onChange={(e) => setDraft({ ...draft, responseMapping: e.target.value })} placeholder='{ "entitlementsPath": "$.items", "fields": {...} }' />
      </Field>

      <Field label={t('App.Admin.Booksellers.EntitlementTab.RetryPolicyLabel')}>
        <textarea className={`${textInput} font-mono text-xs`} rows={3} value={draft.retryPolicy} onChange={(e) => setDraft({ ...draft, retryPolicy: e.target.value })} placeholder='{ "maxAttempts": 3, "backoffMs": 500 }' />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('App.Admin.Booksellers.EntitlementTab.TimeoutLabel')}><input className={textInput} type="number" min={1} value={draft.timeoutSeconds} onChange={(e) => setDraft({ ...draft, timeoutSeconds: Number(e.target.value) || 30 })} /></Field>
        <Field label={t('App.Admin.Booksellers.EntitlementTab.CacheDurationLabel')}><input className={textInput} type="number" min={0} value={draft.cacheDurationMinutes} onChange={(e) => setDraft({ ...draft, cacheDurationMinutes: Number(e.target.value) || 0 })} /></Field>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={() => (data ? update.mutate() : create.mutate())}
          disabled={!canSave || create.isPending || update.isPending}
          className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {data ? t('App.Admin.Booksellers.EntitlementTab.SaveConfig') : t('App.Admin.Booksellers.EntitlementTab.CreateConfig')}
        </button>
      </div>
    </div>
  );
}

const textInput = 'border border-neutral-300 rounded px-2 py-1 text-sm w-full';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function readErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message ?? e?.message ?? 'Save failed';
}
